/**
 * Centralized TanStack Query keys. Keep all keys here so cache invalidations
 * are explicit and discoverable.
 */
export const queryKeys = {
  auth: {
    me: ['auth', 'me'] as const,
    onboarding: ['auth', 'onboarding'] as const,
    brandKit: ['auth', 'brandKit'] as const,
    sessions: ['auth', 'sessions'] as const,
    /** Las aplicaciones de la casa — la rejilla 3×3 de la barra superior. */
    apps: ['auth', 'apps'] as const,
  },
  profile: {
    me: ['profile'] as const,
    activity: (type: string, page: number) =>
      ['profile', 'activity', type, page] as const,
  },
  plans: {
    all: ['plans'] as const,
    current: ['plans', 'current'] as const,
    usage: ['plans', 'usage'] as const,
    invoices: ['plans', 'invoices'] as const,
  },
  companies: {
    all: ['companies'] as const,
    me: ['companies', 'me'] as const,
    detail: (id: number) => ['companies', id] as const,
    managers: (id: number) => ['companies', id, 'managers'] as const,
  },
  brands: {
    all: ['brands'] as const,
    /** Contadores por marca para la galería (`/xk-company/summary`). */
    summary: ['brands', 'summary'] as const,
    detail: (id: number) => ['brands', id] as const,
    /** La página «Legal & Consent» de la marca (`/co-companies/:id/legal`). */
    legal: (id: number) => ['brands', id, 'legal'] as const,
  },
  /** `co_brandkit` — the identity the live game paints itself with. */
  brandKit: {
    byCompany: (companyId: number, appType = 'xotski') =>
      ['brand-kit', { companyId, appType }] as const,
  },
  // "Projects" in the UI map to event groups. Keys are scoped by brand.
  eventGroups: {
    all: ['event-groups'] as const,
    byBrand: (brandId: number) => ['event-groups', { brandId }] as const,
    detail: (id: number) => ['event-groups', id] as const,
    // MEASURE tab — project-wide stock allocation report.
    report: (id: number) => ['event-groups', id, 'report'] as const,
  },
  // Reporters — event-scoped list + the brand-wide distinct list that powers
  // the Planner "reporters you've worked with" panel.
  reporters: {
    byEvent: (eventId: number) => ['reporters', { eventId }] as const,
    byBrand: (brandId: number) => ['reporters', { brandId }] as const,
  },
  // Shared-QR groups (several stations behind one QR).
  qrGroups: {
    byEvent: (eventId: number) => ['qr-groups', { eventId }] as const,
    publicDetail: (id: number) => ['qr-groups', 'public', id] as const,
  },
  stationBrandConfig: {
    byStation: (stationId: number) => ['station-brand-config', { stationId }] as const,
    detail: (id: number) => ['station-brand-config', id] as const,
  },
  // Etiquetas. `root` existe para invalidar por prefijo: al marcar una
  // entidad, toda lista que la contenga queda desfasada.
  tags: {
    root: ['tags'] as const,
    byType: (companyId: number, entityType: string) =>
      ['tags', 'vocab', { companyId, entityType }] as const,
    assignments: (companyId: number, entityType: string, entityIds: number[]) =>
      ['tags', 'assignments', { companyId, entityType, entityIds }] as const,
    match: (companyId: number, entityType: string, tagIds: number[]) =>
      ['tags', 'match', { companyId, entityType, tagIds }] as const,
  },
  // Catálogo de juegos (`g_game`). Transversal: no cuelga de un evento.
  games: {
    available: ['games', 'available'] as const,
  },
  /**
   * Estilos guardados de Xurvey. Transversales como los juegos: son de la
   * empresa y su familia, no de un evento — el mismo estilo se aplica en
   * cualquiera.
   */
  xurvey: {
    stylePresets: ['xurvey', 'style-presets'] as const,
    /** Agregación de una encuesta acotada a unos despliegues (ids ordenados para que la clave sea estable). */
    aggregate: (surveyId: number, linkIds: readonly number[]) =>
      ['xurvey', 'aggregate', surveyId, [...linkIds].sort((a, b) => a - b).join(',')] as const,
    /** Los despliegues (no de prueba) de las estaciones de unos eventos. */
    scopeDeployments: (eventIds: readonly number[]) =>
      ['xurvey', 'scope-deployments', [...eventIds].sort((a, b) => a - b).join(',')] as const,
    posterPresets: ['xurvey', 'poster-presets'] as const,
    /** La capa legal derivada de un despliegue (instrumentos, avisos, candados). */
    consentConfig: (linkId: number) => ['xurvey', 'consent-config', linkId] as const,
  },
  events: {
    all: ['events'] as const,
    byBrand: (brandId: number) => ['events', { brandId }] as const,
    byGroup: (groupId: number) => ['events', { groupId }] as const,
    detail: (id: number) => ['events', id] as const,
    /** El acta de cierre del evento, o su previsualización. */
    closing: (id: number) => ['events', id, 'closing'] as const,
  },
  stations: {
    all: ['stations'] as const,
    byBrand: (brandId: number) => ['stations', { brandId }] as const,
    byEvent: (eventId: number) => ['stations', { eventId }] as const,
    /** Lista completa (500): clave propia para no compartir caché con las páginas de 25. */
    byEventAll: (eventId: number) => ['stations', { eventId, perPage: 500 }] as const,
    detail: (id: number) => ['stations', id] as const,
    summary: (id: number) => ['stations', id, 'summary'] as const,
    responses: (id: number, page: number) =>
      ['stations', id, 'responses', page] as const,
  },
  stock: {
    all: ['stock'] as const,
    byBrand: (brandId: number) => ['stock', { brandId }] as const,
    /** Lista completa (500): clave propia para no compartir caché con las páginas de 25. */
    byBrandAll: (brandId: number) => ['stock', { brandId, perPage: 500 }] as const,
    detail: (id: number) => ['stock', id] as const,
  },
  /** El libro de movimientos del almacén (derivado en el servidor). */
  stockMovements: {
    recent: ['stock-movements', 'recent'] as const,
  },
  /** Bibliotecas de imágenes servidas desde uploads/ (logos de juego, siluetas). */
  library: {
    wheelLogos: ['library', 'wheel-logos'] as const,
    merchOutlines: ['library', 'merch-outlines'] as const,
  },
  stockAllocations: {
    byBrand: (brandId: number) => ['stock-allocations', { brandId }] as const,
    byStation: (stationId: number) => ['stock-allocations', { stationId }] as const,
    byEvent: (eventId: number) => ['stock-allocations', { eventId }] as const,
    /** Lista completa (500): clave propia, no comparte caché con las de 25 ni con el `StockReport`. */
    byEventAll: (eventId: number) => ['stock-allocations', { eventId, perPage: 500 }] as const,
    /** Margen libre por premio en la marca (`/xk-event-stock-station/headroom`). */
    headroom: (stockIds: readonly number[]) => ['stock-allocations', 'headroom', [...stockIds]] as const,
  },
  /** Task 7.2 — a project's stock pool (the planner's warehouse lane). */
  projectStockPool: {
    all: ['project-stock-pool'] as const,
    byProject: (eventGroupId: number) =>
      ['project-stock-pool', { eventGroupId }] as const,
  },
  surveys: {
    all: ['surveys'] as const,
    templates: ['surveys', 'templates'] as const,
    detail: (id: number) => ['surveys', id] as const,
    preview: (id: number) => ['surveys', id, 'preview'] as const,
    publicObject: (surveyId: string, stationId: string) =>
      ['surveys', 'public', surveyId, stationId] as const,
  },
  /** Los hechos de los dashboards (`/xk-event-insights/facts`), por selección de eventos. */
  /** CH-08 · Metas de evento (efectivas, con origen) y de proyecto. */
  goals: {
    all: ['goals'] as const,
    event: (eventId: number) => ['goals', 'event', eventId] as const,
    progress: (eventId: number) => ['goals', 'progress', eventId] as const,
    project: (groupId: number) => ['goals', 'project', groupId] as const,
  },
  insights: {
    /** Prefijo de todas las lecturas de Insights (para invalidar). */
    all: ['insights'] as const,
    facts: (eventIds: readonly number[]) =>
      ['insights', 'facts', [...eventIds].sort((a, b) => a - b).join(',')] as const,
  },
  /** Prefijos que otras pantallas usan en línea (`['eventReport', …]`); aquí sólo para invalidar. */
  eventReport: { all: ['eventReport'] as const },
  liveMonitor: { all: ['liveMonitor'] as const },
  contacts: {
    // Caso 01 — Leads CRM. The `all` key is the legacy mock key; new
    // queries scope by filter snapshot so list invalidations don't blow
    // away the detail cache.
    all: ['contacts'] as const,
    list: (filters: Record<string, unknown>) =>
      ['contacts', 'list', filters] as const,
    detail: (id: number) => ['contacts', 'detail', id] as const,
  },
  favorites: {
    all: ['favorites'] as const,
    byBrand: (brandId: number) => ['favorites', { brandId }] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    inbox: ['notifications', 'inbox'] as const,
    unread: ['notifications', 'unread'] as const,
    sent: ['notifications', 'sent'] as const,
    candidates: ['notifications', 'candidates'] as const,
  },
} as const;
